import type { Treaty } from '@elysiajs/eden'
import { z } from 'zod'

export class ApiV2Error extends Error {
  public readonly status: number
  public readonly digest: string | undefined
  public readonly value: ApiV2ErrorType['value']

  constructor(public readonly error: ApiV2ErrorType) {
    super(getApiV2ErrorMessage(error.value))
    this.status = error.status
    this.value = error.value
  }

  static isApiError(error: unknown): error is ApiV2Error {
    try {
      return ApiV2ErrorSchema.safeParse(JSON.parse(error as string)).success
    } catch {
      return false
    }
  }

  static fromJSON(json: string): ApiV2Error {
    return new ApiV2Error(JSON.parse(json))
  }

  toJSON() {
    return {
      status: this.status,
      digest: this.digest,
      value: this.value,
    }
  }
}

export type ApiV2ErrorType = z.infer<typeof ApiV2ErrorSchema>

const ApiV2ErrorValueSchema = z
  .object({
    error: z.string(),
    success: z.literal(false).optional(),
  })
  .or(
    z.object({
      type: z.literal('validation'),
      on: z.string(),
    }),
  )
  .or(z.string())

export const ApiV2ErrorSchema = z.object({
  status: z.coerce.number(),
  value: ApiV2ErrorValueSchema,
})

const getApiV2ErrorMessage = (value: ApiV2ErrorType['value']): string => {
  if (typeof value === 'string') return value
  if ('type' in value) return value.on
  return value.error
}

export type MaybeFunction<T> = T | ((...a: any) => T)
export type UnwrapMaybeFunction<T> = T extends (...a: any) => infer R ? R : T
export type UnwrapMaybeFunctionParams<T> = T extends (...a: infer R) => any
  ? R
  : T
export type MaybePromise<T> = T | Promise<T>

export async function ensureApiResponse<
  TResponse extends MaybeFunction<MaybePromise<Treaty.TreatyResponse<{}>>>,
  TData extends Treaty.Data<TResponse> = NonNullable<
    Awaited<UnwrapMaybeFunction<TResponse>>['data']
  >,
>(data: TResponse): Promise<TData> {
  const response = (await data) as unknown as NonNullable<
    Awaited<UnwrapMaybeFunction<TResponse>>
  >

  if (response.error) {
    console.error(response.error)
    const schema = ApiV2ErrorSchema.parse(response.error)

    if (schema.status === 401) {
      console.info('redirecting to signin')
    }

    throw new Error(JSON.stringify(schema))
  }

  return response.data as TData
}

export function getErrorMessageFromResponse<
  TResponse extends Treaty.TreatyResponse<{}>,
  TError extends Treaty.Data<TResponse> = NonNullable<
    Awaited<UnwrapMaybeFunction<TResponse>>['error']
  >,
>(data: TResponse): string {
  const error = data.error as unknown as TError

  const parsedError = ApiV2ErrorSchema.parse(
    typeof error === 'string' ? JSON.parse(error) : error,
  )

  return getApiV2ErrorMessage(parsedError.value)
}

export declare namespace TreatyHelpers {
  export type SearchParams<
    Response extends MaybeFunction<MaybePromise<Treaty.TreatyResponse<{}>>>,
  > =
    NonNullable<Awaited<UnwrapMaybeFunctionParams<Response>>> extends [
      any,
      infer Params,
      ...any,
    ]
      ? Params extends { query: infer Query }
        ? Query
        : never
      : never

  export type Body<
    Response extends MaybeFunction<MaybePromise<Treaty.TreatyResponse<{}>>>,
  > =
    NonNullable<Awaited<UnwrapMaybeFunctionParams<Response>>> extends [
      infer Body,
      ...any,
    ]
      ? Body
      : never
}
